import { ArticleExtraData } from "../../../../../../../articlePage/stateInterfaces";
import { MediaContentTypes } from "../../../../../../../constants/contentTypes";
import { SiteItemData, SiteVideoData } from "../../../../../../interfaces";
import { VideoBehaiforData } from "../../../../../../commonDataObjects/interfaces";
import { ArticleDataJsonVer2, ItemJsonVer2, MediaDataJsonVersion2 } from "../../../../../../jsonSerializer/widgetJsonInterfacesVer2";
import { ImageDataJson } from "../../../../../../jsonSerializer/widgetJsonInterfaces";

const DEFAULT_BACKGROUND_IMAGE = "https://ynet-pic1.yit.co.il/Common/images/mivzak_reka.jpg";

const DEFAULT_VIDEO_BEHAVIOR: VideoBehaiforData = {
    isAutoPlaying: true,
    isLoopPlaying: false,
    isPlayerBarVisible: false,
    isSoundButtonVisible: false,
    isMuteAvailable: false,
};

export function mapItemJsonToArticleExtraData(article: ItemJsonVer2): ArticleExtraData {
    const { title, subTitle, roof, roofCount, text, linkData, mediaData } = article;
    const link = linkData.link as Partial<ArticleDataJsonVer2>;
    const { id, specialData, hasVideo, displayVideoIcon, catId, catName, mainCatId, subchannelName, pubDate, href, author } = link;

    const media = mediaData && mediaData.length > 0 ? mediaData[0] as MediaDataJsonVersion2 : null;
    let promotionImageDetails = null;
    let video = undefined;

    if (media && media.type === "video" && media.item) {
        const videoItem = media.item as any;
        video = [{
            videoName: title,
            credit: videoItem.credit || "",
            main_link: videoItem.url,
            low_res_link: videoItem.url,
            duration: videoItem.duration,
            promoPath: videoItem.promoUrl || "",
            aspectRatio: videoItem.mediaProportion || "",
            mediaVideoId: String(videoItem.videoId || ""),
            posterSrc: videoItem.opening_img && videoItem.opening_img.src ? videoItem.opening_img.src : videoItem.src,
        }];
    }

    if (media && media.type === "image") {
        const imageItem = media.item as ImageDataJson;
        promotionImageDetails = {
            url: imageItem.src,
            alt: imageItem.alt || "",
            credit: imageItem.credit || "",
            publishedLink: imageItem.src || "",
            caption: (imageItem as any).caption || "",
            aspectRatio: (imageItem as any).mediaProportion || "",
        };
    }

    return {
        articleId: String(id || ""),
        title,
        subTitle,
        roof,
        roofCount,
        plainText: text,
        author,
        publishedLink: href,
        dateUpdatedOnSite: pubDate,
        launchDate: pubDate,
        dateUpdated: specialData && specialData.dateUpdatedOnSite,
        promotionImage: promotionImageDetails ? promotionImageDetails.url || "" : "",
        promotionImageDetails,
        firstArticleImageDetails: null,
        hasVideo,
        displayVideoIcon,
        video,
        specialData,
        category: catName ? { id: catId, textToShow: catName } as any : null,
        premiumExpireDays: 0,
        subchannelName,
    } as ArticleExtraData;
}

export function mapArticleExtraDataToOverlayItems(
    articles: ArticleExtraData[],
    verticalBackgroundImage?: string,
): SiteItemData[] {
    if (!articles || articles.length === 0) return [];

    return articles.map(article => {
        const { articleId, roof, title, plainText, author, video, publishedLink, promotionImageDetails, specialData, dateUpdatedOnSite } = article;
        const videoCredit = (specialData && specialData.videoCredit) || (video && video[0] ? video[0].credit : "");
        const videoOriginalCaption = specialData && specialData.videoOriginalCaption;

        return {
            roofTitle: roof,
            title,
            text: plainText,
            author,
            itemId: articleId,
            publishedLink,
            dateUpdatedOnSite,
            media: buildMedia(title, video, videoCredit, videoOriginalCaption, promotionImageDetails, verticalBackgroundImage),
        } as SiteItemData;
    });
}

function buildMedia(
    title: string,
    video: any[],
    videoCredit: string,
    videoOriginalCaption: string,
    promotionImageDetails: any,
    verticalBackgroundImage?: string,
) {
    let videoMedia: SiteVideoData;
    let imageMedia: any;

    if (video && video[0]) {
        videoMedia = {
            ...DEFAULT_VIDEO_BEHAVIOR,
            credit: videoCredit,
            url: video[0].main_link,
            poster: video[0].posterSrc,
            duration: video[0].duration,
            videoType: "vertical_video",
            promoPath: video[0].promoPath,
            mediaId: video[0].mediaVideoId,
            videoPageId: video[0].videoPageId,
            aspectRatio: video[0].aspectRatio,
            downGradeUrl: video[0].low_res_link,
            title: title || videoOriginalCaption,
            mediaType: MediaContentTypes.MEDIA_VIDEO,
        } as SiteVideoData;
    }

    if (promotionImageDetails) {
        const isDefault = promotionImageDetails.alt === "default image";
        const backgroundImage = verticalBackgroundImage || DEFAULT_BACKGROUND_IMAGE;

        imageMedia = {
            isDefaultImage: isDefault,
            alt: promotionImageDetails.alt || "",
            mediaType: MediaContentTypes.MEDIA_IMAGE,
            credit: promotionImageDetails.credit || "",
            caption: promotionImageDetails.caption || "",
            url: isDefault ? backgroundImage : promotionImageDetails.url,
            publishedLink: isDefault ? backgroundImage : promotionImageDetails.publishedLink,
            aspectRatio: isDefault ? "9:16" : getAspectRatio(promotionImageDetails),
        };
    }

    return videoMedia ? videoMedia : imageMedia;
}

const getAspectRatio = (promotionImageDetails: any) => {
    if (promotionImageDetails.aspectRatio && promotionImageDetails.aspectRatio !== "") {
        return promotionImageDetails.aspectRatio;
    } else if (promotionImageDetails.width && promotionImageDetails.height) {
        return promotionImageDetails.height > promotionImageDetails.width ? "9:16" : "16:9";
    } else return ""
};

export interface OngoingRoofItemMeta {
    isEventRoof: boolean
    showFilledRedDot: boolean
    showWhiteDot: boolean
    showTimeline: boolean
    hideRoof: boolean
    isClusterStart: boolean
    isInMultiCluster: boolean
    showClusterHeader: boolean
    /** Updates pill. Only the topmost flash of the roof. */
    showUpdatesButton: boolean
    /** Highest roofCount among visible items of this roof. */
    updatesCount: number
    clusterSize: number
    clusterRoof: string | null
}

const emptyOngoingMeta = (): OngoingRoofItemMeta => ({
    isEventRoof: false,
    showFilledRedDot: false,
    showWhiteDot: false,
    showTimeline: false,
    hideRoof: false,
    isClusterStart: false,
    isInMultiCluster: false,
    showClusterHeader: false,
    showUpdatesButton: false,
    updatesCount: 0,
    clusterSize: 1,
    clusterRoof: null,
});

/** Trims and collapses excess whitespace so roof titles match for clustering. */
export function normalizeRoofTitle(roof: string | null | undefined): string {
    if (!roof) {
        return "";
    }
    return roof.trim() //.replace(/\s+/g, " "); possible solution to remove excess whitespace inside the string
}

function roofUpdateCount(article: ArticleExtraData): number {
    const count = Number(article && article.roofCount);
    return isNaN(count) ? 0 : count;
}

/**
 * Ongoing-event presentation for the visible homepage ticker.
 * The feature flag is applied by the caller. A roof is an ongoing event when
 * any visible item of that roof has roofCount >= 2. Page 184 is not read here.
 * Consecutive items of that roof form a cluster, including a run of one.
 * Every cluster shows the same roof title. The updates pill and the filled red
 * dot stay on the topmost flash of that roof, even when it is a single item
 * and a larger cluster of the same roof sits lower. Later flashes get a white dot.
 * A one-item top flash does not also show a white dot.
 */
export function buildOngoingRoofPresentation(articles: ArticleExtraData[], excludedRoofs: string[] = []): OngoingRoofItemMeta[] {
    if (!articles || articles.length === 0) {
        return [];
    }

    const excluded = (excludedRoofs || []).map(normalizeRoofTitle).filter(Boolean);
    const eventRoofs: { [roof: string]: true } = {};
    const topIndexByRoof: { [roof: string]: number } = {};
    const updatesCountByRoof: { [roof: string]: number } = {};

    articles.forEach((article, index) => {
        const roof = normalizeRoofTitle(article.roof);
        if (!roof || excluded.indexOf(roof) !== -1) {
            return;
        }

        if (topIndexByRoof[roof] === undefined) {
            topIndexByRoof[roof] = index;
        }

        const count = roofUpdateCount(article);
        if (count > (updatesCountByRoof[roof] || 0)) {
            updatesCountByRoof[roof] = count;
        }
        if (count >= 2) {
            eventRoofs[roof] = true;
        }
    });

    const result: OngoingRoofItemMeta[] = articles.map(() => emptyOngoingMeta());
    let i = 0;
    while (i < articles.length) {
        const roof = normalizeRoofTitle(articles[i].roof);
        if (!roof || !eventRoofs[roof]) {
            i++;
            continue;
        }

        let end = i;
        while (end < articles.length && normalizeRoofTitle(articles[end].roof) === roof) {
            end++;
        }

        const clusterSize = end - i;
        const topIndex = topIndexByRoof[roof];
        const isTopCluster = topIndex >= i && topIndex < end;
        const updatesCount = updatesCountByRoof[roof] || clusterSize;

        for (let k = i; k < end; k++) {
            const isTopItem = k === topIndex;
            result[k] = {
                isEventRoof: true,
                showFilledRedDot: isTopItem,
                showWhiteDot: !(clusterSize === 1 && isTopItem),
                showTimeline: true,
                hideRoof: true,
                isClusterStart: k === i,
                isInMultiCluster: clusterSize >= 2,
                showClusterHeader: true,
                showUpdatesButton: isTopCluster,
                updatesCount,
                clusterSize,
                clusterRoof: roof,
            };
        }
        i = end;
    }

    return result;
}

export function filterArticlesByRoof(articles: ArticleExtraData[], roof: string): ArticleExtraData[] {
    if (!roof || !articles || articles.length === 0) {
        return [];
    }
    const normalizedRoof = normalizeRoofTitle(roof);
    return articles.filter(article => normalizeRoofTitle(article.roof) === normalizedRoof);
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

function is24HoursLimitDisabled(): boolean {
    return typeof window !== "undefined" && !!window.disable24hourslimit;
}

export function isArticleWithinLast24Hours(article: ArticleExtraData): boolean {
    const updatedTime = new Date(article.dateUpdatedOnSite).getTime();
    if (isNaN(updatedTime)) {
        return false;
    }
    return (Date.now() - updatedTime) <= TWENTY_FOUR_HOURS_MS;
}

/** Roof-matched popup items, limited to the last 24 hours unless `window.disable24hourslimit` is set. */
export function filterOngoingRoofPopupArticles(articles: ArticleExtraData[], roof: string): ArticleExtraData[] {
    const relatedArticles = filterArticlesByRoof(articles, roof);
    if (is24HoursLimitDisabled()) {
        return relatedArticles;
    }
    return relatedArticles.filter(isArticleWithinLast24Hours);
}
